import { useQuery } from '@apollo/client';
import type { FetchAlertBannersResponse } from '@generated/graphql';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Button } from '@ovComponents/2-component/button/button';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { UpdateOrganizationSwitch } from '@ovComponents/4-module/updateOrganizationSwitch/updateOrganizationSwitch';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { FETCH_ALERT_BANNERS } from './alertBanners.queries';
import AlertBannerFilterModal from './components/alertBannerFilterModal';
import { BannerCell } from './components/bannerCell';
import {
  BASE_TABLE,
  DEFAULT_PAGE_SIZE,
  sortableTableHeaderKeyMap,
  TableHeaderKeys,
} from './constants';
import { useAlertBannerPermissions } from './hooks/useAlertBannerPermissions';
import { type AlertBannerRow, AlertBannerStatus } from './types';
import { getAlertBannerStatus } from './utils';

export const AlertBanners = () => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['alertBanners']);

  const [page, setPage] = usePageState(1, 'page');
  const [sortField, setSortField] = usePageState('createdAt', 'sf');
  const [sortDesc, setSortDesc] = usePageState(true, 'sd');
  const [pageSize, setPageSize] = usePageState(DEFAULT_PAGE_SIZE, 'ps');
  const [filteredStatuses, setFilteredStatuses] = useState<AlertBannerStatus[]>(
    Object.values(AlertBannerStatus),
  );

  const { loading, data, refetch } = useQuery<{
    fetchAlertBanners: FetchAlertBannersResponse;
  }>(FETCH_ALERT_BANNERS, {
    variables: {
      input: {
        filter: {
          statuses: filteredStatuses,
          organization: activeOrganization.id,
        },
        pagination: {
          sortField,
          sortDesc,
          perPage: pageSize,
          offSet: (page - 1) * pageSize,
        },
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { hasReadBannersPermission, hasWriteBannersPermission } =
    useAlertBannerPermissions();
  const hasWriteAdvancedOrgSettingsPermission = permissions.includes(
    'write:advanced_organization_settings',
  );

  const totalCount = data?.fetchAlertBanners.totalCount;
  const alertBanners: AlertBannerRow[] | undefined =
    data?.fetchAlertBanners.alertBanners.map((banner) => ({
      ...banner,
      status: getAlertBannerStatus(
        banner.state,
        banner.startDate,
        banner.endDate,
      ),
    }));

  const onSort = (field: string) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    }
    setSortField(field);
  };

  const sortDirection = (field?: string) =>
    sortField === field ? (sortDesc ? 'desc' : 'asc') : undefined;

  const tableHeaders = BASE_TABLE.filter(
    (header) =>
      !header.permissions?.length ||
      header.permissions.some((requiredPermission) =>
        permissions.includes(requiredPermission),
      ),
  );

  if (!hasWriteBannersPermission && !hasReadBannersPermission) return null;
  return (
    <Card loading={loading}>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          {hasWriteAdvancedOrgSettingsPermission && (
            <UpdateOrganizationSwitch
              field='useParentAlertBanners'
              label={t('useParentAlertBanners')}
              organizationId={activeOrganization.id}
              refetchQueries={[FETCH_ALERT_BANNERS]}
            />
          )}
        </Box>
        <Box display='flex' justifyContent='end'>
          <Box display='flex' gap={2}>
            <AlertBannerFilterModal
              filterExists={
                filteredStatuses.length !==
                Object.values(AlertBannerStatus).length
              }
              initialStatuses={filteredStatuses}
              onApply={setFilteredStatuses}
            />
            {hasWriteBannersPermission && (
              <Button
                component={Link}
                to='./new'
                leadingIcon={AddIcon}
                label={t('table.newBanner')}
              />
            )}
          </Box>
        </Box>
      </CardContent>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableBody>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableHeadCell
                key={header.key}
                isSortable={header.isSortable}
                onClick={() =>
                  header.isSortable && sortableTableHeaderKeyMap[header.key]
                    ? onSort(sortableTableHeaderKeyMap[header.key]!)
                    : undefined
                }
                sortDirection={sortDirection(
                  sortableTableHeaderKeyMap[header.key],
                )}
                right={[
                  TableHeaderKeys.Status,
                  TableHeaderKeys.Organization,
                ].includes(header.key)}
                center={[
                  TableHeaderKeys.RoleProfiles,
                  TableHeaderKeys.LastUpdated,
                  TableHeaderKeys.ActiveDateRange,
                  TableHeaderKeys.Actions,
                ].includes(header.key)}
              >
                {t(header.labelPath)}
              </TableHeadCell>
            ))}
          </TableRow>
          {loading
            ? [...Array(15)].map((_x, i: number) => (
                <TableRow key={`skeleton-${i}`}>
                  {tableHeaders.map((header, idx) => (
                    <TableCell
                      key={`skeleton-${i}-${header.key}`}
                      dense
                      isFirst={idx === 0}
                    >
                      <Skeleton width='100%' height='16px' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : alertBanners?.map((banner) => (
                <TableRow
                  key={banner.id}
                  hover
                  sx={{
                    '&:hover': {
                      cursor:
                        banner.organization.id === activeOrganization.id
                          ? 'pointer'
                          : 'default',
                    },
                  }}
                  onClick={() => {
                    if (
                      banner.organization.id === activeOrganization.id &&
                      (hasReadBannersPermission || hasWriteBannersPermission)
                    ) {
                      navigate(`/settings/alertBanners/${banner.id}`);
                    }
                  }}
                >
                  {tableHeaders.map((header, idx) => (
                    <BannerCell
                      key={header.key}
                      headerKey={header.key}
                      alertBanner={banner}
                      isFirst={idx === 0}
                      refetch={refetch}
                    />
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {!!totalCount && totalCount > 0 && (
        <Box display='flex' justifyContent='end' my={2}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(_e, newPage) => setPage(newPage)}
            perPage={pageSize}
            onChangePerPage={(newPageSize) => setPageSize(newPageSize)}
            disabled={loading}
            size='small'
          />
        </Box>
      )}
    </Card>
  );
};
